import {VwpLicense} from "../store/generated/api";

export function filterAvailableLicenses(licenses: VwpLicense[]): VwpLicense[] {
    return licenses.filter(license => {
        const available = license.available !== undefined
            ? license.available
            : (license.quantity && license.assigned ? license.quantity - license.assigned : 0);
        return available > 0;
    });
}

export function getLicenseDisplayStatus(license: VwpLicense): 'active' | 'disabled' | 'expiring' | 'expired' {
    if (license.status === 'DISABLED') {
        return 'disabled';
    }

    if (license.status === 'ACTIVE') {
        const expiryDate = license.effectiveExpiryDate;

        if (expiryDate) {
            const expiry = new Date(expiryDate);
            const now = new Date();
            const thirtyDaysFromNow = new Date();
            thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

            // Check if license is already expired
            if (expiry <= now) {
                return 'expired';
            }

            // Check if license is expiring within 30 days
            if (expiry <= thirtyDaysFromNow) {
                return 'expiring';
            }
        }
        return 'active';
    }

    // Fallback for any unexpected status
    return 'disabled';
}

/**
 * Check if a license is active and not expired
 */
export function isLicenseActiveAndNotExpired(license: VwpLicense): boolean {
    if (license.status !== 'ACTIVE') return false;

    const expiryDate = license.effectiveExpiryDate;

    if (!expiryDate) return false;

    return new Date(expiryDate) > new Date();
}

/**
 * Get badge variant for license status display
 */
export function getLicenseStatusBadgeVariant(status: 'active' | 'disabled' | 'expiring' | 'expired'): 'default' | 'destructive' | 'secondary' {
    switch (status) {
        case 'active':
            return 'default';
        case 'expiring':
            return 'secondary';
        case 'expired':
            return 'destructive';
        case 'disabled':
            return 'destructive';
        default:
            return 'default';
    }
}

export interface LicenseExpirationDisplay {
    date: string | undefined;
    type: 'support' | 'software';
    isExpiredSupport: boolean;
}

export function getLicenseExpirationDisplay(license: VwpLicense): LicenseExpirationDisplay {
    const now = new Date();

    const supportExpiration = license.supportExpiration ? new Date(license.supportExpiration) : null;
    const softwareExpiration = license.softwareExpiration ? new Date(license.softwareExpiration) : null;

    if (supportExpiration && supportExpiration > now) {
        return {
            date: license.supportExpiration,
            type: 'support',
            isExpiredSupport: false
        };
    }

    if (softwareExpiration && softwareExpiration > now) {
        return {
            date: license.softwareExpiration,
            type: 'software',
            isExpiredSupport: true
        };
    }

    return {
        date: license.softwareExpiration,
        type: 'software',
        isExpiredSupport: false
    };
}
